import {
    Component,
    Output,
    EventEmitter,
    OnInit,
    OnDestroy
} from "@angular/core";
import { AppEventsService as Events } from "../../services/app-events.service";
import { AuthService } from "../../services/auth/auth.service";
import { AuthUser } from "../../entities/AuthUser";
import { TranslateService } from "@ngx-translate/core";
import { ModalController } from "@ionic/angular";
import { Capacitor } from "@capacitor/core";
import { DocumentNavigationModalComponent } from "../document-navigation-modal/document-navigation-modal.component";
import { UnreadReportsService } from "../../services/unread-reports/unread-reports.service";
import { Subscription } from "rxjs";

@Component({
    selector: "app-navbar",
    templateUrl: "./navbar.component.html",
    styleUrls: ["./navbar.component.scss"]
})
export class NavbarComponent implements OnInit, OnDestroy {
    public visible = true;
    public profile = this.authService.userProfileState.getValue();

    public menuItems = [];
    public baseMenuItems = [];
    public unreadReportsCount = 0;
    @Output() menuStateChange = new EventEmitter();
    private modal: HTMLIonModalElement;
    private unreadReportsSubscription: Subscription;
    private userProfileSubscription: Subscription;
    private languageChangeSubscription: Subscription;

    constructor(
        public events: Events,
        private authService: AuthService,
        private translateService: TranslateService,
        private modalController: ModalController,
        private unreadReportsService: UnreadReportsService
    ) {
        this.events.subscribe("nav:hide", () => {
            this.visible = false;
        });

        this.events.subscribe("nav:show", () => {
            this.visible = true;
        });

        // The profile decides which items are visible, so rebuild the menu
        // whenever it changes (division, employee function, permissions).
        this.userProfileSubscription =
            this.authService.userProfileState.subscribe(
                (authUser: AuthUser) => {
                    this.profile = authUser;
                    this.getMenuItems();
                }
            );

        // The labels themselves are rendered through the translate pipe, but
        // the order depends on the translated text, so rebuild on a language
        // change as well.
        this.languageChangeSubscription =
            this.translateService.onLangChange.subscribe(() => {
                this.getMenuItems();
            });
    }

    ngOnInit() {
        this.unreadReportsSubscription =
            this.unreadReportsService.unreadReportsState$.subscribe(
                ({ count }) => {
                    this.unreadReportsCount = count;
                }
            );
    }

    ngOnDestroy() {
        if (this.unreadReportsSubscription) {
            this.unreadReportsSubscription.unsubscribe();
        }

        if (this.userProfileSubscription) {
            this.userProfileSubscription.unsubscribe();
        }

        if (this.languageChangeSubscription) {
            this.languageChangeSubscription.unsubscribe();
        }
    }

    get isDesktop(): boolean {
        return !Capacitor.isNativePlatform();
    }

    async showDocumentsMenu() {
        this.modal = await this.modalController.create({
            component: DocumentNavigationModalComponent,
            cssClass: "document-navigation-modal",
            animated: true,
            breakpoints: !this.isDesktop ? [0, 1] : undefined,
            initialBreakpoint: 1,
            canDismiss: true,
            handle: false,
            mode: "md",
            componentProps: {
                callback: this.changeStatus.bind(this)
            }
        });

        return await this.modal.present();
    }

    async changeStatus() {
        await this.modal.dismiss();
    }

    getMenuItems() {
        this.baseMenuItems = [
            {
                name: "navigation.news",
                routerLink: "/news",
                routerLinkOptions: { exact: true },
                icon: "baseline-photo-24px",
                hasIf: true
            },
            {
                name: "navigation.contacts",
                routerLink: "/contact-departments",
                routerLinkOptions: {},
                icon: "contacts",
                hasIf: true
            },
            {
                name: "navigation.documents",
                routerLink: null,
                routerLinkOptions: {},
                icon: "baseline-description-24px",
                hasIf: true
            }
        ].filter((item) => {
            return item.hasIf;
        });

        this.menuItems = [
            {
                name: "navigation.events",
                routerLink: "/events",
                routerLinkOptions: {},
                icon: "cake",
                hasIf: true
            },
            {
                name: "navigation.profile",
                routerLink: "/profile",
                routerLinkOptions: {},
                icon: "mdi_account_circle",
                hasIf: true
            },
            {
                name: "navigation.locations-on-route",
                routerLink: "/tank-locations-car-washes",
                routerLinkOptions: {},
                icon: "mdi_local_gas_station",
                hasIf: this.hasPermission("tanklocaties-wasstraten")
            },
            {
                name: "navigation.expenses",
                routerLink: "/expenses",
                routerLinkOptions: {},
                icon: "mdi_expenses",
                hasIf: this.hasPermission("expenses")
            },
            {
                name: "navigation.colleagues",
                routerLink: "/colleagues",
                routerLinkOptions: {},
                icon: "contacts",
                hasIf: true
            },
            {
                name: "navigation.ideas",
                routerLink: "/ideas",
                routerLinkOptions: {},
                icon: "ideas",
                hasIf: true
            },
            {
                name: "navigation.furlough",
                routerLink: "/furloughs",
                routerLinkOptions: {},
                icon: "furlough",
                hasIf: this.hasPermission("furlough")
            },
            {
                name: "navigation.my-service-schedule",
                routerLink: "/content-page/1",
                routerLinkOptions: {},
                icon: "calendar-nav",
                hasIf: this.profile?.can_access_my_service_schedule
            },
            {
                name: "navigation.my-favorite-articles",
                routerLink: "/news/favorite",
                routerLinkOptions: { exact: true },
                icon: "baseline-photo-24px",
                hasIf: true
            },
            {
                name: "navigation.datev",
                routerLink: "/content-page/2",
                routerLinkOptions: {},
                icon: "baseline-photo-24px",
                hasIf: this.profile?.can_access_datev
            }
        ]
            .filter((item) => {
                return item.hasIf;
            })
            // localeCompare orders accented and upper case letters the way the
            // active language expects; `<` compares UTF-16 code units and
            // never reports two labels as equal.
            .sort((a, b) =>
                this.label(a.name).localeCompare(
                    this.label(b.name),
                    this.translateService.currentLang
                )
            );
    }

    /**
     * Translated label of a menu item, used to order the menu.
     *
     * The template renders the key through the translate pipe, so only the
     * ordering needs the text here.
     */
    private label(key: string): string {
        return this.translateService.instant(key);
    }

    /**
     * Permission check that tolerates the empty profile the state starts with,
     * so building the menu can never throw while the real profile is loading.
     */
    private hasPermission(permission: string): boolean {
        if (!this.profile?.business_unit_permissions) {
            return false;
        }

        return this.profile.hasPermission(permission);
    }

    toggleMenu() {
        this.menuStateChange.emit();
    }
}
